import React from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SyncIcon from '@mui/icons-material/Sync';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentsIcon from '@mui/icons-material/Payments';
import LanguageIcon from '@mui/icons-material/Language';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import axiosAPI from 'Includes/api/api';
import { getRequest, postRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL } from 'Includes/urls';
import { TEAM_STATUS_CHOICES } from 'Includes/teamStatus';
import ReportsMobileBottomNav from 'Components/ReportsMobileBottomNav';

const TEAM_STATUS_LABELS = Object.fromEntries(
  TEAM_STATUS_CHOICES.map((opt) => [opt.value, opt.label])
);

const SUMMARY_COLUMN_KEYS = [
  'institute_name',
  'school_address',
  'payment_gateway_enabled',
  'fee_collection_last_used_value',
  'attendance_used_percentage',
  'diary_used_percentage',
  'last_exam_used_percentage',
  'using_efficiency',
  'coordinator_name',
];

const ALWAYS_VISIBLE_COLUMNS = new Set(['institute_name', 'school_address', 'payment_gateway_enabled']);

const formatValue = (value, columnKey, isNotCreated) => {
  if (columnKey === 'payment_gateway_enabled') {
    return (
      <Chip
        label={value ? "Enabled" : "Disabled"}
        color={value ? "success" : "default"}
        size="small"
        variant="outlined"
        sx={{ fontWeight: 700, fontSize: '0.7rem', height: 20 }}
      />
    );
  }
  if (isNotCreated && !ALWAYS_VISIBLE_COLUMNS.has(columnKey)) {
    return <Typography component="span" sx={{ color: 'error.main' }}>Not Created</Typography>;
  }
  if (value === 'Never Used') {
    return <Typography component="span" sx={{ color: 'error.main' }}>{value}</Typography>;
  }
  if (columnKey === 'using_efficiency') {
    const isGreen = typeof value === 'number' && value > 50;
    return <Typography component="span" sx={{ color: isGreen ? 'success.main' : 'error.main', fontWeight: 600 }}>{value}%</Typography>;
  }
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const formatMoney = (val) => {
  if (val === null || val === undefined || val === '') return '0.00';
  const num = Number(val);
  if (Number.isNaN(num)) return String(val);
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatInt = (val) => {
  if (val === null || val === undefined || val === '') return '0';
  const num = Number(val);
  return Number.isNaN(num) ? String(val) : Math.round(num).toLocaleString('en-IN');
};

// Clean format badge helper for detailed metrics
const renderMetricBadge = (value, isNotCreated) => {
  if (isNotCreated) {
    return <Chip label="Not Created" size="small" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.7rem', height: 20 }} />;
  }
  if (value === 'Never Used') {
    return <Chip label="Never Used" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: '0.7rem', height: 20 }} />;
  }
  if (value === null || value === undefined || value === '' || value === '-') {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }
  if (typeof value === 'number') {
    return <Typography variant="body2" fontWeight={700} color="#0f172a">{value}</Typography>;
  }
  return <Typography variant="body2" fontWeight={600} color="#1e293b">{String(value)}</Typography>;
};

const isFutureAcademicYear = (tabName) => {
  if (!tabName) return false;
  const match = String(tabName).match(/\b(19\d\d|20\d\d)\b/);
  if (!match) return false;
  const startYear = parseInt(match[1], 10);
  const now = new Date();
  const currentAcadStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return startYear > currentAcadStart;
};

const MATRIX_FEATURE_COLUMNS = [
  { key: 'attendance', label: 'Attendance', shortLabel: 'Attnd' },
  { key: 'staffAttendance', label: 'Staff Attnd', shortLabel: 'Staff' },
  { key: 'feeCollection', label: 'Fees Collection', shortLabel: 'Fees' },
  { key: 'paymentGateway', label: 'Payment Gateway', shortLabel: 'PG' },
  { key: 'onlineCollections', label: 'PG Txns', shortLabel: 'Online ₹' },
  { key: 'website', label: 'Website', shortLabel: 'Web' },
  { key: 'biometric', label: 'Biometric', shortLabel: 'Bio' },
  { key: 'diary', label: 'Diary / HW', shortLabel: 'Diary' },
  { key: 'examSchedule', label: 'Exam Schedule', shortLabel: 'Exam' },
  { key: 'examMarks', label: 'Exam Marks', shortLabel: 'Marks' },
  { key: 'store', label: 'Store / Items', shortLabel: 'Store' },
  { key: 'expenses', label: 'Expenses', shortLabel: 'Expense' },
  { key: 'activePastWeek', label: 'Active (7D)', shortLabel: '7D Active' },
];

const evaluateFeatureStatus = (row, featureKey) => {
  const isNotCreated = Boolean(row?.is_not_created);
  const d = row?.report_data || row || {};

  if (isNotCreated) {
    return { has: false, label: 'Not Created', detail: 'Report not created for this academic year' };
  }

  switch (featureKey) {
    case 'attendance': {
      const val = d.student_attendance_last_used_value || d.attendance_used_percentage;
      const s = String(val || '').trim();
      const has = Boolean(s && !['never used', 'never_used', '-', '0', '0%'].includes(s.toLowerCase()));
      return {
        has,
        label: has ? (d.attendance_used_percentage ? `${d.attendance_used_percentage}%` : 'Used') : 'Never Used',
        detail: has ? `Attendance marked: ${s}` : 'Student attendance never recorded',
      };
    }
    case 'staffAttendance': {
      const val = d.staff_attendance_last_marked_value;
      const s = String(val || '').trim();
      const has = Boolean(s && !['never used', 'never_used', '-', '0'].includes(s.toLowerCase()));
      return {
        has,
        label: has ? 'Marked' : 'Never Used',
        detail: has ? `Staff attendance marked: ${s}` : 'Staff attendance never marked',
      };
    }
    case 'feeCollection': {
      const val = d.fee_collection_last_used_value;
      const s = String(val || '').trim();
      const has = Boolean(s && !['never used', 'never_used', '-', '0'].includes(s.toLowerCase()));
      return {
        has,
        label: has ? 'Used' : 'Never Used',
        detail: has ? `Last fee collected: ${s}` : 'Fee collection module never used',
      };
    }
    case 'paymentGateway': {
      const has = Boolean(row?.payment_gateway_enabled);
      const pgData = row?.payment_gateway_data;
      const amt = Number(pgData?.total_amount || 0);
      return {
        has,
        label: has ? 'Enabled' : 'Disabled',
        detail: has
          ? `Online payment gateway enabled. Total collected: ₹${amt.toLocaleString('en-IN')}`
          : 'Payment gateway disabled for this school',
      };
    }
    case 'onlineCollections': {
      const pgData = row?.payment_gateway_data;
      const amt = Number(pgData?.total_amount || 0);
      const tx = Number(pgData?.transaction_count || 0);
      const has = Boolean(amt > 0 || tx > 0);
      return {
        has,
        label: has ? `₹${amt >= 100000 ? (amt / 100000).toFixed(1) + 'L' : amt.toLocaleString('en-IN')}` : '0 Txns',
        detail: has
          ? `${tx} transactions completed totaling ₹${amt.toLocaleString('en-IN')}`
          : 'No online payment transactions recorded yet',
      };
    }
    case 'website': {
      const has = Boolean(row?.website_enabled);
      return {
        has,
        label: has ? 'Enabled' : 'Disabled',
        detail: has ? 'Institutional web portal is active' : 'Website module is not enabled',
      };
    }
    case 'biometric': {
      const has = Boolean(row?.biometric_enabled);
      return {
        has,
        label: has ? 'Enabled' : 'Disabled',
        detail: has ? 'Biometric attendance hardware connected' : 'Biometric devices disabled/not configured',
      };
    }
    case 'diary': {
      const val = d.student_diary_last_used_value || d.diary_used_percentage;
      const s = String(val || '').trim();
      const has = Boolean(s && !['never used', 'never_used', '-', '0', '0%'].includes(s.toLowerCase()));
      return {
        has,
        label: has ? (d.diary_used_percentage ? `${d.diary_used_percentage}%` : 'Used') : 'Never Used',
        detail: has ? `Diary/Homework active: ${s}` : 'Student diary never used',
      };
    }
    case 'examSchedule': {
      const val = d.exam_schedule_last_used_value;
      const s = String(val || '').trim();
      const has = Boolean(s && !['never used', 'never_used', '-', '0'].includes(s.toLowerCase()));
      return {
        has,
        label: has ? 'Used' : 'Never Used',
        detail: has ? `Exam schedule created: ${s}` : 'Exam schedule never configured',
      };
    }
    case 'examMarks': {
      const val = d.exam_marks_last_used_value;
      const s = String(val || '').trim();
      const has = Boolean(s && !['never used', 'never_used', '-', '0'].includes(s.toLowerCase()));
      return {
        has,
        label: has ? 'Used' : 'Never Used',
        detail: has ? `Exam marks entered: ${s}` : 'Exam marks never recorded',
      };
    }
    case 'store': {
      const val = d.item_sold_last_marked_value;
      const s = String(val || '').trim();
      const has = Boolean(s && !['never used', 'never_used', '-', '0'].includes(s.toLowerCase()));
      return {
        has,
        label: has ? 'Used' : 'Never Used',
        detail: has ? `Items sold recorded: ${s}` : 'Store module never used',
      };
    }
    case 'expenses': {
      const val = d.expenses_last_marked_value;
      const s = String(val || '').trim();
      const has = Boolean(s && !['never used', 'never_used', '-', '0'].includes(s.toLowerCase()));
      return {
        has,
        label: has ? 'Used' : 'Never Used',
        detail: has ? `Expenses recorded: ${s}` : 'Expense module never used',
      };
    }
    case 'activePastWeek': {
      const staff7d = Number(d.staff_active_last_7_days_value || 0);
      const student7d = Number(d.student_active_last_7_days_value || 0);
      const total7d = staff7d + student7d;
      const has = total7d > 0;
      return {
        has,
        label: has ? `${total7d} Users` : '0 Active',
        detail: has ? `${staff7d} staff and ${student7d} students logged in during past 7 days` : 'No user login activity in past 7 days',
      };
    }
    default:
      return { has: false, label: '-', detail: '' };
  }
};

const MatrixPill = ({ status }) => {
  const { has, detail } = status;
  return (
    <Tooltip title={detail || (has ? 'Active / Configured' : 'Missing / Inactive')} arrow placement="top">
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          borderRadius: '50%',
          fontSize: '0.9rem',
          fontWeight: 900,
          cursor: 'default',
          transition: 'all 0.15s ease',
          backgroundColor: has ? '#dcfce7' : '#fee2e2',
          color: has ? '#15803d' : '#dc2626',
          border: '1px solid',
          borderColor: has ? '#86efac' : '#fca5a5',
          boxShadow: has ? '0 1px 3px rgba(21,128,61,0.15)' : '0 1px 3px rgba(220,38,38,0.15)',
          '&:hover': {
            transform: 'scale(1.15)',
            boxShadow: has ? '0 2px 8px rgba(21,128,61,0.3)' : '0 2px 8px rgba(220,38,38,0.3)',
          },
        }}
      >
        <span>{has ? '✓' : '✗'}</span>
      </Box>
    </Tooltip>
  );
};

function ClientReportList(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = React.useState([]);
  const [sourceColumns, setSourceColumns] = React.useState([]);
  const [tabs, setTabs] = React.useState([]);
  const [syncDates, setSyncDates] = React.useState([]);
  const [syncDateFilter, setSyncDateFilter] = React.useState('');
  const [syncDatesReady, setSyncDatesReady] = React.useState(false);
  const [supervisorFilter, setSupervisorFilter] = React.useState('');
  const [supervisorOptions, setSupervisorOptions] = React.useState([]);
  const [featuresFilter, setFeaturesFilter] = React.useState([]);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);
  const listScrollRef = React.useRef(null);
  const [tabFilter, setTabFilter] = React.useState('');
  const [tabDefaultApplied, setTabDefaultApplied] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [rowCount, setRowCount] = React.useState(0);
  const [expandedRowId, setExpandedRowId] = React.useState(null);
  const [lastSyncTime, setLastSyncTime] = React.useState(null);
  const [moduleFilter, setModuleFilter] = React.useState('');
  const [moduleValueFilter, setModuleValueFilter] = React.useState('');
  const [moduleDistinctValues, setModuleDistinctValues] = React.useState([]);
  const [coordinatorFilter, setCoordinatorFilter] = React.useState('');
  const [coordinatorOptions, setCoordinatorOptions] = React.useState([]);
  const [creationStatusFilter, setCreationStatusFilter] = React.useState('');
  const [statusByTeamFilter, setStatusByTeamFilter] = React.useState('all');
  const [sortBy, setSortBy] = React.useState('institute_name');
  const [sortDir, setSortDir] = React.useState('asc');
  const [allSchools, setAllSchools] = React.useState([]);
  const [downloadingNeverUsed, setDownloadingNeverUsed] = React.useState(false);
  const [urlParamsApplied, setUrlParamsApplied] = React.useState(false);

  // NEW: Split-Screen View vs Classic Table View vs Report Generator
  const [viewMode, setViewMode] = React.useState('split'); // 'split' | 'table' | 'generator'
  const [selectedRowKey, setSelectedRowKey] = React.useState(null);
  const [showRawColumns, setShowRawColumns] = React.useState(false);
  const [quickSchoolSearch, setQuickSchoolSearch] = React.useState('');

  // Report Generator (Feature Audit Matrix) States
  const [matrixRows, setMatrixRows] = React.useState([]);
  const [matrixLoading, setMatrixLoading] = React.useState(false);
  const [matrixSearch, setMatrixSearch] = React.useState('');
  const [matrixMissingFilter, setMatrixMissingFilter] = React.useState('all');
  const [matrixPage, setMatrixPage] = React.useState(0);
  const [matrixRowsPerPage, setMatrixRowsPerPage] = React.useState(50);
  const [exportingMatrix, setExportingMatrix] = React.useState(false);

  const userInfo = React.useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const isSuperuser = Boolean(userInfo?.is_superuser);

  // Unique row key generator
  const getRowKey = React.useCallback(
    (row) => `${row?.tab_name || ''}::${(row?.institute_name || row?.id || '').trim().toLowerCase()}`,
    []
  );

  // Read URL params on mount
  React.useEffect(() => {
    if (urlParamsApplied) return;
    const params = new URLSearchParams(window.location.search);
    const urlModule = params.get('module') || '';
    const urlModuleValue = params.get('module_value') || '';
    const urlTabName = params.get('tab_name') || '';
    const urlCreationStatus = params.get('creation_status') || '';
    const urlStatusByTeam = params.get('status_by_team') || '';
    const urlSyncDate = params.get('sync_date') || '';
    const urlSupervisor = params.get('supervisor') || '';
    const urlFeatures = params.get('features') || '';
    const urlView = params.get('view') || params.get('view_mode') || '';
    if (urlView && ['split', 'table', 'generator'].includes(urlView)) {
      setViewMode(urlView);
    }
    if (urlModule) setModuleFilter(urlModule);
    if (urlModuleValue) setModuleValueFilter(urlModuleValue);
    if (urlTabName) {
      setTabFilter(urlTabName);
      setTabDefaultApplied(true);
    }
    if (urlCreationStatus) setCreationStatusFilter(urlCreationStatus);
    if (urlStatusByTeam) setStatusByTeamFilter(urlStatusByTeam || 'all');
    if (urlSyncDate) setSyncDateFilter(urlSyncDate);
    if (urlSupervisor) setSupervisorFilter(urlSupervisor);
    if (urlFeatures) setFeaturesFilter(urlFeatures.split(',').filter(Boolean));
    setUrlParamsApplied(true);
  }, [urlParamsApplied]);

  const fetchMatrixData = React.useCallback(() => {
    if (!urlParamsApplied || !syncDatesReady) return;
    setMatrixLoading(true);
    getRequest(
      GET_URL.client_track_report.api,
      {
        matrix_report: 1,
        ...(syncDateFilter && { sync_date: syncDateFilter }),
        ...(tabFilter && { tab_name: tabFilter }),
        ...(supervisorFilter && { supervisor: supervisorFilter }),
        ...(coordinatorFilter && { coordinator: coordinatorFilter }),
        ...(statusByTeamFilter && statusByTeamFilter !== 'all' && { status_by_team: statusByTeamFilter }),
        ...(creationStatusFilter && { creation_status: creationStatusFilter }),
        ...(featuresFilter.length > 0 && { features: featuresFilter.join(',') }),
        ...(moduleFilter && moduleValueFilter && {
          module_filter: moduleFilter,
          module_value: moduleValueFilter,
        }),
      },
      props
    )
      .then((response) => {
        setMatrixLoading(false);
        if (response?.status === 200) {
          setMatrixRows(response.data?.data || []);
          if (!tabFilter && response.data?.default_tab) {
            setTabFilter(response.data.default_tab);
          }
        }
      })
      .catch(() => setMatrixLoading(false));
  }, [
    urlParamsApplied,
    syncDatesReady,
    syncDateFilter,
    tabFilter,
    supervisorFilter,
    coordinatorFilter,
    statusByTeamFilter,
    creationStatusFilter,
    featuresFilter,
    moduleFilter,
    moduleValueFilter,
    props,
  ]);

  React.useEffect(() => {
    if (viewMode === 'generator') {
      fetchMatrixData();
    }
  }, [viewMode, fetchMatrixData]);

  const handleExportMatrixExcel = async () => {
    try {
      setExportingMatrix(true);
      const params = {
        matrix_export: 1,
        export_format: 'excel',
        ...(syncDateFilter && { sync_date: syncDateFilter }),
        ...(tabFilter && { tab_name: tabFilter }),
        ...(supervisorFilter && { supervisor: supervisorFilter }),
        ...(coordinatorFilter && { coordinator: coordinatorFilter }),
        ...(statusByTeamFilter && statusByTeamFilter !== 'all' && { status_by_team: statusByTeamFilter }),
        ...(creationStatusFilter && { creation_status: creationStatusFilter }),
        ...(featuresFilter.length > 0 && { features: featuresFilter.join(',') }),
        ...(matrixSearch.trim() && { search: matrixSearch.trim() }),
      };

      const response = await axiosAPI.get(GET_URL.client_track_report.api, {
        params,
        responseType: 'blob',
      });

      if (response && response.status === 200) {
        const blob = response.data instanceof Blob
          ? response.data
          : new Blob([response.data], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
        const fileURL = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = fileURL;
        const cleanTab = (tabFilter || 'all').replace(/[^a-zA-Z0-9_-]/g, '_');
        downloadLink.download = `institution_feature_matrix_${cleanTab}.xlsx`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
        setTimeout(() => window.URL.revokeObjectURL(fileURL), 1000);
      }
    } catch (err) {
      console.error('Failed to export matrix excel', err);
    } finally {
      setExportingMatrix(false);
    }
  };

  const handleExportMatrixCSV = handleExportMatrixExcel;

  const filteredMatrixRows = React.useMemo(() => {
    let list = matrixRows;
    if (matrixSearch.trim()) {
      const q = matrixSearch.trim().toLowerCase();
      list = list.filter((r) =>
        (r.institute_name || '').toLowerCase().includes(q) ||
        (r.coordinator_name || '').toLowerCase().includes(q) ||
        (r.school_address || '').toLowerCase().includes(q)
      );
    }
    if (matrixMissingFilter !== 'all') {
      list = list.filter((r) => {
        if (matrixMissingFilter === 'missing_pg') return !evaluateFeatureStatus(r, 'paymentGateway').has;
        if (matrixMissingFilter === 'missing_attendance') return !evaluateFeatureStatus(r, 'attendance').has;
        if (matrixMissingFilter === 'missing_staff_attendance') return !evaluateFeatureStatus(r, 'staffAttendance').has;
        if (matrixMissingFilter === 'missing_biometric') return !evaluateFeatureStatus(r, 'biometric').has;
        if (matrixMissingFilter === 'missing_website') return !evaluateFeatureStatus(r, 'website').has;
        if (matrixMissingFilter === 'missing_fee') return !evaluateFeatureStatus(r, 'feeCollection').has;
        if (matrixMissingFilter === 'missing_diary') return !evaluateFeatureStatus(r, 'diary').has;
        if (matrixMissingFilter === 'missing_exam') return !evaluateFeatureStatus(r, 'examSchedule').has;
        if (matrixMissingFilter === 'low_efficiency') return !r.is_not_created && parseFloat(r.using_efficiency || 0) < 50;
        if (matrixMissingFilter === 'high_efficiency') return !r.is_not_created && parseFloat(r.using_efficiency || 0) >= 70;
        if (matrixMissingFilter === 'not_created') return Boolean(r.is_not_created);
        return true;
      });
    }
    return list;
  }, [matrixRows, matrixSearch, matrixMissingFilter]);

  const paginatedMatrixRows = React.useMemo(() => {
    const start = matrixPage * matrixRowsPerPage;
    return filteredMatrixRows.slice(start, start + matrixRowsPerPage);
  }, [filteredMatrixRows, matrixPage, matrixRowsPerPage]);

  const matrixSummaryCards = React.useMemo(() => {
    return MATRIX_FEATURE_COLUMNS.map((col) => {
      let ticks = 0;
      let crosses = 0;
      for (const r of filteredMatrixRows) {
        if (evaluateFeatureStatus(r, col.key).has) {
          ticks += 1;
        } else {
          crosses += 1;
        }
      }
      return {
        key: col.key,
        label: col.label,
        ticks,
        crosses,
        total: filteredMatrixRows.length,
      };
    });
  }, [filteredMatrixRows]);

  // Fetch sync dates and default to latest
  React.useEffect(() => {
    getRequest(
      GET_URL.client_track_report.api,
      { sync_dates: 1 },
      props
    ).then((response) => {
      if (response?.status === 200) {
        const dates = response.data?.data || [];
        setSyncDates(dates);
        setSyncDateFilter((current) => {
          if (current && dates.some((d) => d.date === current)) return current;
          return dates[0]?.date || '';
        });
      }
      setSyncDatesReady(true);
    }).catch(() => setSyncDatesReady(true));
  }, [props]);

  // Fetch supervisor options
  React.useEffect(() => {
    getRequest(
      GET_URL.client_track_report.api,
      { supervisor_options: 1 },
      props
    ).then((response) => {
      if (response?.status === 200) {
        setSupervisorOptions(response.data?.data || []);
      }
    }).catch(() => {});
  }, [props]);

  // Fetch coordinator options (optionally filtered by supervisor)
  React.useEffect(() => {
    getRequest(
      GET_URL.client_track_report.api,
      {
        coordinator_options: 1,
        ...(supervisorFilter && { supervisor: supervisorFilter }),
      },
      props
    ).then((response) => {
      if (response?.status === 200) {
        const coords = response.data?.data || [];
        setCoordinatorOptions(coords);
        setCoordinatorFilter((current) => {
          if (!current) return '';
          return coords.some((c) => String(c.id) === String(current)) ? current : '';
        });
      }
    }).catch(() => {});
  }, [supervisorFilter, props]);

  // Fetch distinct values when module filter changes
  React.useEffect(() => {
    if (!moduleFilter) {
      setModuleDistinctValues([]);
      return;
    }
    getRequest(
      GET_URL.client_track_report.api,
      {
        distinct_values: 1,
        column: moduleFilter,
        ...(tabFilter && { tab_name: tabFilter }),
      },
      props
    ).then((response) => {
      if (response?.status === 200) {
        setModuleDistinctValues(response.data?.data || []);
      }
    }).catch(() => {});
  }, [moduleFilter, tabFilter, props]);

  const fetchReports = React.useCallback(
    (pageToFetch = 0, isAppend = false) => {
      if (!urlParamsApplied || !syncDatesReady) return;
      if (isAppend) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      getRequest(
        GET_URL.client_track_report.api,
        {
          limit: pageSize,
          pageno: pageToFetch + 1,
          ...(syncDateFilter && { sync_date: syncDateFilter }),
          ...(tabFilter && { tab_name: tabFilter }),
          ...(supervisorFilter && { supervisor: supervisorFilter }),
          ...(coordinatorFilter && { coordinator: coordinatorFilter }),
          ...(statusByTeamFilter && statusByTeamFilter !== 'all' && { status_by_team: statusByTeamFilter }),
          ...(creationStatusFilter && { creation_status: creationStatusFilter }),
          ...(featuresFilter.length > 0 && { features: featuresFilter.join(',') }),
          ...(moduleFilter && moduleValueFilter && {
            module_filter: moduleFilter,
            module_value: moduleValueFilter,
          }),
          ...(sortBy && { sort: sortBy, sort_dir: sortDir }),
        },
        props
      )
        .then((response) => {
          if (isAppend) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }

          if (response?.status === 200) {
            if (!tabDefaultApplied) {
              const defaultTab = response.data?.default_tab || '';
              setTabDefaultApplied(true);
              if (defaultTab && !tabFilter) {
                setTabFilter(defaultTab);
                return;
              }
            }

            const apiRows = response.data?.data || [];
            const serverCount = response.data?.count;
            const finalCount = serverCount !== undefined && serverCount !== null ? serverCount : apiRows.length;
            setRowCount(finalCount);

            if (isAppend) {
              setRows((prev) => {
                const seen = new Set(prev.map((r) => (r.institute_name || '').trim().toLowerCase()));
                const additional = [];
                for (const r of apiRows) {
                  const key = (r.institute_name || '').trim().toLowerCase();
                  if (!key || seen.has(key)) continue;
                  seen.add(key);
                  additional.push(r);
                }
                const combined = [...prev, ...additional];
                setHasMore(combined.length < finalCount && additional.length > 0);
                return combined;
              });
              setPage(pageToFetch);
            } else {
              const seen = new Set();
              const uniqueRows = [];
              for (const r of apiRows) {
                const key = (r.institute_name || '').trim().toLowerCase();
                if (!key || seen.has(key)) continue;
                seen.add(key);
                uniqueRows.push(r);
              }
              setRows(uniqueRows);
              setPage(0);
              setHasMore(uniqueRows.length < finalCount && apiRows.length > 0);
              if (listScrollRef.current) {
                listScrollRef.current.scrollTop = 0;
              }
            }

            setSourceColumns(response.data?.columns || []);
            setTabs((response.data?.tabs || []).filter((t) => !isFutureAcademicYear(t)));
            if (Array.isArray(response.data?.sync_dates) && response.data.sync_dates.length) {
              setSyncDates(response.data.sync_dates);
            }

            setLastSyncTime(response.data?.last_sync_time || null);
            setExpandedRowId(null);
          }
        })
        .catch(() => {
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [
      featuresFilter,
      pageSize,
      props,
      tabFilter,
      supervisorFilter,
      coordinatorFilter,
      statusByTeamFilter,
      creationStatusFilter,
      moduleFilter,
      moduleValueFilter,
      syncDateFilter,
      urlParamsApplied,
      syncDatesReady,
      sortBy,
      sortDir,
      tabDefaultApplied,
    ]
  );

  // Trigger initial / non-append fetch whenever filters change
  React.useEffect(() => {
    fetchReports(0, false);
  }, [fetchReports]);

  // Infinite Scroll Trigger for Left Pane
  const loadMore = React.useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    fetchReports(page + 1, true);
  }, [loading, loadingMore, hasMore, page, fetchReports]);

  const handleListScroll = React.useCallback(
    (e) => {
      if (viewMode !== 'split' || loading || loadingMore || !hasMore) return;
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 150) {
        loadMore();
      }
    },
    [viewMode, loading, loadingMore, hasMore, loadMore]
  );

  // Download never used CSV
  const handleDownloadNeverUsed = () => {
    if (!moduleFilter) return;

    setDownloadingNeverUsed(true);
    getRequest(
      GET_URL.client_track_report.api,
      {
        never_used_export: 1,
        module: moduleFilter,
        ...(supervisorFilter && { supervisor: supervisorFilter }),
        ...(coordinatorFilter && { coordinator: coordinatorFilter }),
        ...(tabFilter && { tab_name: tabFilter }),
      },
      { ...props, responseType: 'blob' }
    )
      .then((response) => {
        if (response?.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
          const fileURL = window.URL.createObjectURL(blob);
          const contentDisposition =
            response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition'];
          let filename = `client_report_never_used_${moduleFilter}.csv`;
          if (typeof contentDisposition === 'string') {
            const match = contentDisposition.match(/filename="?([^"]+)"?/i);
            if (match?.[1]) filename = match[1];
          }
          const link = document.createElement('a');
          link.href = fileURL;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(fileURL);
          document.body.removeChild(link);
        }
      })
      .finally(() => setDownloadingNeverUsed(false));
  };

  const handleSync = () => {
    const transactionId = Date.now();
    setSyncing(true);
    setMessage('');
    postRequest(POST_URL.client_track_report.api, { transaction_id: transactionId }, props).then((response) => {
      setSyncing(false);
      if (response?.status === 200) {
        setLastSyncTime(response.data?.last_sync_time || lastSyncTime);
        setMessage('Report sync started. Data will update after the callback is received.');
      }
    }).catch(() => {
      setSyncing(false);
    });
  };

  const uniqueTabs = React.useMemo(() => {
    const seen = new Set();
    return (tabs || []).filter((tab) => {
      const key = (tab || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [tabs]);

  const summaryColumns = React.useMemo(() => {
    const instituteColumn = (sourceColumns || []).find(
      (item) => item.schemacolumn === 'institute_name'
    );

    if (moduleFilter) {
      const selectedModuleColumn = (sourceColumns || []).find(
        (item) => item.schemacolumn === moduleFilter
      );
      const addressColumn = (sourceColumns || []).find(
        (item) => item.schemacolumn === 'school_address'
      );

      const cols = [];
      if (instituteColumn) cols.push(instituteColumn);
      if (addressColumn) cols.push(addressColumn);
      if (
        selectedModuleColumn &&
        selectedModuleColumn.schemacolumn !== 'institute_name' &&
        selectedModuleColumn.schemacolumn !== 'school_address'
      ) {
        cols.push(selectedModuleColumn);
      }
      return cols;
    }

    const selectedColumns = (sourceColumns || []).filter((item) =>
      SUMMARY_COLUMN_KEYS.includes(item.schemacolumn)
    );

    return selectedColumns.length
      ? selectedColumns
      : (sourceColumns || []).slice(0, 6);
  }, [sourceColumns, moduleFilter]);

  const detailColumns = React.useMemo(() => {
    const summaryKeys = new Set(summaryColumns.map((item) => item.schemacolumn));
    return (sourceColumns || []).filter((item) => !summaryKeys.has(item.schemacolumn));
  }, [sourceColumns, summaryColumns]);

  const totalColumnCount = summaryColumns.length + 2;

  const activeModuleLabel = React.useMemo(() => {
    if (!moduleFilter) return '';
    const col = (sourceColumns || []).find((c) => c.schemacolumn === moduleFilter);
    return col ? col.column : moduleFilter;
  }, [moduleFilter, sourceColumns]);

  const activeSupervisorLabel = React.useMemo(() => {
    if (!supervisorFilter) return '';
    const opt = supervisorOptions.find((s) => String(s.id) === String(supervisorFilter));
    return opt ? opt.name : supervisorFilter;
  }, [supervisorFilter, supervisorOptions]);

  const activeCoordinatorLabel = React.useMemo(() => {
    if (!coordinatorFilter) return '';
    const opt = coordinatorOptions.find((c) => String(c.id) === String(coordinatorFilter));
    return opt ? opt.name : coordinatorFilter;
  }, [coordinatorFilter, coordinatorOptions]);

  const activeSyncDateLabel = React.useMemo(() => {
    if (!syncDateFilter) return '';
    const entry = syncDates.find((d) => d.date === syncDateFilter);
    return entry?.label || syncDateFilter;
  }, [syncDateFilter, syncDates]);

  const handleSort = (field) => {
    setPage(0);
    if (sortBy === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(field);
    setSortDir('asc');
  };

  const renderSortableHeader = (field, label) => (
    <TableCell
      key={field}
      sortDirection={sortBy === field ? sortDir : false}
      sx={{ fontWeight: 700, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
        hideSortIcon={false}
        sx={{
          fontWeight: 700,
          '& .MuiTableSortLabel-icon': { fontSize: 16, opacity: 0.7 },
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  // Active selected client row for the Split View
  const activeRow = React.useMemo(() => {
    if (!rows.length) return null;
    if (!selectedRowKey) return rows[0];
    const found = rows.find((r) => getRowKey(r) === selectedRowKey);
    return found || rows[0];
  }, [rows, selectedRowKey, getRowKey]);

  // Filtered schools for left pane quick search
  const visibleRows = React.useMemo(() => {
    if (!quickSchoolSearch.trim()) return rows;
    const term = quickSchoolSearch.toLowerCase();
    return rows.filter((r) =>
      (r.institute_name || '').toLowerCase().includes(term) ||
      (r.coordinator_name || '').toLowerCase().includes(term)
    );
  }, [rows, quickSchoolSearch]);

  // Chart data for selected client
  const clientChartData = React.useMemo(() => {
    if (!activeRow || activeRow.is_not_created) return [];
    return [
      { name: 'Attendance', value: parseFloat(activeRow.attendance_used_percentage || 0), fill: '#2563eb' },
      { name: 'Homework', value: parseFloat(activeRow.diary_used_percentage || 0), fill: '#059669' },
      { name: 'Exams', value: parseFloat(activeRow.last_exam_used_percentage || 0), fill: '#7c3aed' },
      { name: 'Efficiency', value: parseFloat(activeRow.using_efficiency || 0), fill: '#f59e0b' },
    ];
  }, [activeRow]);

  return (
    <Box p={{ xs: 1.5, sm: 2.5 }} maxWidth={1440} mx="auto">
      {/* ============================================================== */}
      {/* 1. STICKY TOP FILTER CONTROLS & VIEW SWITCHER                  */}
      {/* ============================================================== */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: { xs: 66, sm: 66 },
          zIndex: 90,
          p: { xs: 1.5, sm: 2 },
          mb: 2.5,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.08)',
        }}
      >
        {/* Top Header & Action Row */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={1.5}>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={800} color="#0f172a">
                Client Report
              </Typography>
              {lastSyncTime && (
                <Chip
                  size="small"
                  label={`Synced: ${new Date(lastSyncTime).toLocaleDateString()}`}
                  sx={{ fontSize: '0.7rem', fontWeight: 600, bgcolor: '#f1f5f9' }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Review institution usage reports, module adoption, and efficiency.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {/* View Mode Toggle: Split-View vs Classic Table vs Report Generator */}
            <ToggleButtonGroup
              size="small"
              value={viewMode}
              exclusive
              onChange={(e, next) => next && setViewMode(next)}
              sx={{ backgroundColor: '#f8fafc', borderRadius: 2 }}
            >
              <ToggleButton value="split" sx={{ textTransform: 'none', px: 1.5, py: 0.5, fontWeight: 700, gap: 0.5 }}>
                <ViewSidebarOutlinedIcon sx={{ fontSize: 18 }} />
                Split View
              </ToggleButton>
              <ToggleButton value="table" sx={{ textTransform: 'none', px: 1.5, py: 0.5, fontWeight: 700, gap: 0.5 }}>
                <TableChartOutlinedIcon sx={{ fontSize: 18 }} />
                Table View
              </ToggleButton>
              <ToggleButton
                value="generator"
                sx={{
                  textTransform: 'none',
                  px: 1.5,
                  py: 0.5,
                  fontWeight: 700,
                  gap: 0.5,
                  ...(viewMode === 'generator' && {
                    backgroundColor: '#1e293b !important',
                    color: '#ffffff !important',
                  }),
                }}
              >
                <FactCheckOutlinedIcon sx={{ fontSize: 18 }} />
                Report Generator
              </ToggleButton>
            </ToggleButtonGroup>



            {moduleFilter && viewMode !== 'generator' && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={handleDownloadNeverUsed}
                disabled={downloadingNeverUsed}
                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
              >
                {downloadingNeverUsed ? 'Downloading...' : 'Export Never Used'}
              </Button>
            )}

            {isSuperuser && (
              <Button
                size="small"
                variant="contained"
                startIcon={<SyncIcon sx={{ fontSize: 16 }} />}
                onClick={handleSync}
                disabled={syncing}
                sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700 }}
              >
                {syncing ? 'Syncing...' : 'Sync Report'}
              </Button>
            )}
          </Stack>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Filter Controls Row */}
        <Grid container spacing={1.5} alignItems="center">
          {/* Sync Date */}
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              select
              fullWidth
              size="small"
              label="Sync Date"
              value={syncDateFilter}
              onChange={(e) => {
                setSyncDateFilter(e.target.value);
                setPage(0);
              }}
              disabled={!syncDates.length}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              {syncDates.length === 0 && <MenuItem value="">No syncs yet</MenuItem>}
              {syncDates.map((d) => (
                <MenuItem key={d.date} value={d.date}>
                  {d.label} {d.saved_count != null ? `(${d.saved_count} records)` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Supervisor Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Supervisor"
              value={supervisorFilter}
              onChange={(e) => {
                setSupervisorFilter(e.target.value);
                setPage(0);
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">All Supervisors</MenuItem>
              {supervisorOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.name || opt.id}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Coordinator */}
          <Grid item xs={6} sm={4} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Coordinator"
              value={coordinatorFilter}
              onChange={(e) => {
                setCoordinatorFilter(e.target.value);
                setPage(0);
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">All Coordinators</MenuItem>
              {coordinatorOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.name || opt.id}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Features Multiselect Filter */}
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="features-filter-label">Features</InputLabel>
              <Select
                labelId="features-filter-label"
                id="features-filter-select"
                multiple
                value={featuresFilter}
                onChange={(e) => {
                  const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  setFeaturesFilter(val);
                  setPage(0);
                }}
                label="Features"
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return 'All Features';
                  }
                  const labelMap = {
                    payment_gateway: 'Payment Gateway',
                    website: 'Website',
                    biometric: 'Biometric',
                  };
                  return selected.map((s) => labelMap[s] || s).join(', ');
                }}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="payment_gateway">
                  <Checkbox size="small" checked={featuresFilter.indexOf('payment_gateway') > -1} />
                  <ListItemText primary="Payment Gateway" />
                </MenuItem>
                <MenuItem value="website">
                  <Checkbox size="small" checked={featuresFilter.indexOf('website') > -1} />
                  <ListItemText primary="Website" />
                </MenuItem>
                <MenuItem value="biometric">
                  <Checkbox size="small" checked={featuresFilter.indexOf('biometric') > -1} />
                  <ListItemText primary="Biometric" />
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Academic Year */}
          <Grid item xs={6} sm={4} md={1.7}>
            <TextField
              select
              fullWidth
              size="small"
              label="Academic Year"
              value={tabFilter}
              onChange={(e) => {
                setTabFilter(e.target.value);
                setPage(0);
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">All Years</MenuItem>
              {uniqueTabs.map((tab) => (
                <MenuItem key={tab} value={tab}>{tab}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Team Status */}
          <Grid item xs={12} sm={4} md={1.8}>
            <TextField
              select
              fullWidth
              size="small"
              label="Team Status"
              value={statusByTeamFilter}
              onChange={(e) => {
                setStatusByTeamFilter(e.target.value);
                setPage(0);
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              {TEAM_STATUS_CHOICES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {/* Active Filter Chips */}
        {(featuresFilter.length > 0 || moduleFilter || supervisorFilter || coordinatorFilter || tabFilter || creationStatusFilter || (statusByTeamFilter && statusByTeamFilter !== 'all') || syncDateFilter) && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap alignItems="center">
            <FilterListIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
            {syncDateFilter && (
              <Chip label={`Sync: ${activeSyncDateLabel}`} size="small" color="primary" variant="filled" />
            )}
            {supervisorFilter && (
              <Chip
                label={`Supervisor: ${activeSupervisorLabel}`}
                size="small"
                color="secondary"
                variant="filled"
                onDelete={() => {
                  setSupervisorFilter('');
                  setPage(0);
                }}
              />
            )}
            {featuresFilter.length > 0 && (
              <Chip
                label={`Features: ${featuresFilter.map((f) => (f === 'payment_gateway' ? 'Payment Gateway' : f === 'website' ? 'Website' : 'Biometric')).join(', ')}`}
                size="small"
                color="primary"
                variant="filled"
                onDelete={() => {
                  setFeaturesFilter([]);
                  setPage(0);
                }}
              />
            )}
            {tabFilter && (
              <Chip label={`Year: ${tabFilter}`} size="small" color="primary" variant="outlined" onDelete={() => { setTabFilter(''); setPage(0); }} />
            )}
            {coordinatorFilter && (
              <Chip label={`Coordinator: ${activeCoordinatorLabel}`} size="small" color="secondary" variant="outlined" onDelete={() => { setCoordinatorFilter(''); setPage(0); }} />
            )}
            {moduleFilter && (
              <Chip label={`Module: ${activeModuleLabel}`} size="small" color="info" variant="outlined" onDelete={() => { setModuleFilter(''); setModuleValueFilter(''); }} />
            )}
            {moduleValueFilter && (
              <Chip label={`Value: ${moduleValueFilter}`} size="small" variant="outlined" onDelete={() => { setModuleValueFilter(''); setPage(0); }} />
            )}
            {creationStatusFilter && (
              <Chip label={`Report: ${creationStatusFilter === 'not_created' ? 'Not Created' : 'Created'}`} size="small" color="success" variant="outlined" onDelete={() => { setCreationStatusFilter(''); setPage(0); }} />
            )}
            {statusByTeamFilter && statusByTeamFilter !== 'all' && (
              <Chip label={`Team: ${TEAM_STATUS_LABELS[statusByTeamFilter] || statusByTeamFilter}`} size="small" variant="outlined" onDelete={() => { setStatusByTeamFilter('all'); setPage(0); }} />
            )}
          </Stack>
        )}
      </Paper>

      {message && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{message}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Box
        component="style"
        dangerouslySetInnerHTML={{
          __html: `
            @media print {
              header, nav, .MuiPaper-root.filter-sticky, .no-print, button, .MuiBottomNavigation-root { display: none !important; }
              .print-container { border: none !important; box-shadow: none !important; padding: 0 !important; width: 100% !important; max-width: 100% !important; }
              .MuiTableContainer-root { max-height: none !important; overflow: visible !important; border: 1px solid #ccc !important; }
              @page { size: landscape; margin: 8mm; }
            }
          `,
        }}
      />

      {/* ============================================================== */}
      {/* 2. REPORT GENERATOR (INSTITUTION FEATURE AUDIT MATRIX)         */}
      {/* ============================================================== */}
      {viewMode === 'generator' ? (
        <Paper
          elevation={0}
          className="print-container"
          sx={{
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            p: { xs: 2, sm: 2.5 },
          }}
        >
          {/* Header Banner */}
          <Box
            sx={{
              p: 2.2,
              mb: 2.5,
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              color: '#ffffff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <AssignmentTurnedInOutlinedIcon sx={{ color: '#38bdf8', fontSize: 26 }} />
                <Typography variant="h6" fontWeight={800} color="#ffffff">
                  Institution Adoption & Feature Audit Matrix Report
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Complete audit breakdown across all institutions with green ticks (<span style={{ color: '#4ade80', fontWeight: 800 }}>✓</span>) for active/enabled features and red crosses (<span style={{ color: '#f87171', fontWeight: 800 }}>✗</span>) for missing/never used modules.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                size="medium"
                variant="contained"
                startIcon={<DownloadOutlinedIcon sx={{ fontSize: 20 }} />}
                onClick={handleExportMatrixExcel}
                disabled={exportingMatrix}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  px: 2.5,
                  py: 1,
                  bgcolor: '#107c41',
                  color: '#ffffff',
                  boxShadow: '0 4px 14px rgba(16,124,65,0.35)',
                  '&:hover': { bgcolor: '#0b5a30', boxShadow: '0 6px 18px rgba(16,124,65,0.45)' },
                }}
              >
                {exportingMatrix ? 'Generating Excel Sheet...' : 'Download Excel Sheet (.xlsx)'}
              </Button>
            </Stack>
          </Box>

          {/* Stats Adoption Summary Strip */}
          <Paper
            elevation={0}
            sx={{
              p: 1.8,
              mb: 2.5,
              borderRadius: 2,
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2}>
              <Typography variant="caption" fontWeight={800} color="#475569" textTransform="uppercase" letterSpacing={0.5}>
                Feature Adoption Summary ({filteredMatrixRows.length} Institutions Filtered)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Legend: <span style={{ color: '#16a34a', fontWeight: 800 }}>✓ Having / Active</span> · <span style={{ color: '#dc2626', fontWeight: 800 }}>✗ Not Having / Inactive</span>
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                pb: 0.5,
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 3 },
              }}
            >
              {matrixSummaryCards.map((col) => {
                const pct = col.total > 0 ? Math.round((col.ticks / col.total) * 100) : 0;
                return (
                  <Box
                    key={col.key}
                    sx={{
                      p: 1,
                      minWidth: 130,
                      borderRadius: 1.5,
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="#64748b" display="block" noWrap>
                      {col.label}
                    </Typography>
                    <Box display="flex" alignItems="baseline" gap={0.8} mt={0.3}>
                      <Typography variant="body2" fontWeight={800} color="#15803d">
                        {col.ticks} ✓
                      </Typography>
                      <Typography variant="caption" color="#dc2626" fontWeight={700}>
                        {col.crosses} ✗
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontWeight: 700 }}>
                        {pct}%
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>

          {/* Interactive In-Matrix Toolbar */}
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={2} className="no-print">
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" flexGrow={1}>
              <TextField
                size="small"
                placeholder="Quick filter institution or coordinator..."
                value={matrixSearch}
                onChange={(e) => setMatrixSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: matrixSearch && (
                    <IconButton size="small" onClick={() => setMatrixSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
                sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <TextField
                select
                size="small"
                label="Missing Feature Filter"
                value={matrixMissingFilter}
                onChange={(e) => setMatrixMissingFilter(e.target.value)}
                sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                <MenuItem value="all">All Institutions</MenuItem>
                <MenuItem value="missing_pg">Missing Payment Gateway (✗)</MenuItem>
                <MenuItem value="missing_attendance">Missing Student Attendance (✗)</MenuItem>
                <MenuItem value="missing_staff_attendance">Missing Staff Attendance (✗)</MenuItem>
                <MenuItem value="missing_biometric">Missing Biometric (✗)</MenuItem>
                <MenuItem value="missing_website">Missing Website (✗)</MenuItem>
                <MenuItem value="missing_fee">Missing Fee Collection (✗)</MenuItem>
                <MenuItem value="missing_diary">Missing Diary / Homework (✗)</MenuItem>
                <MenuItem value="missing_exam">Missing Exam Schedule (✗)</MenuItem>
                <MenuItem value="low_efficiency">Low Adoption (&lt; 50%)</MenuItem>
                <MenuItem value="high_efficiency">High Adoption (≥ 70%)</MenuItem>
                <MenuItem value="not_created">Report Not Created</MenuItem>
              </TextField>
            </Box>

            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Showing <strong>{paginatedMatrixRows.length}</strong> of <strong>{filteredMatrixRows.length}</strong> institutions
            </Typography>
          </Box>

          {/* Matrix Table */}
          {matrixLoading ? (
            <Box py={8} textAlign="center">
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary" mt={1.5} fontWeight={600}>
                Compiling multi-institution feature matrix...
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)', border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 800, fontSize: '0.72rem', color: '#334155', borderBottom: '2px solid #e2e8f0', py: 1.2 } }}>
                    <TableCell width={40}>#</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Institution Name</TableCell>
                    <TableCell sx={{ minWidth: 130 }}>Coordinator</TableCell>
                    <TableCell sx={{ minWidth: 80 }}>Efficiency</TableCell>
                    {MATRIX_FEATURE_COLUMNS.map((col) => (
                      <TableCell key={col.key} align="center" sx={{ minWidth: 100 }}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedMatrixRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4 + MATRIX_FEATURE_COLUMNS.length} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          No institutions match the current filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedMatrixRows.map((row, index) => {
                      const efficiency = row.is_not_created ? 0 : parseFloat(row.using_efficiency || 0);
                      const rowKey = getRowKey(row);
                      return (
                        <TableRow
                          key={rowKey || index}
                          hover
                          sx={{
                            cursor: 'pointer',
                            '&:nth-of-type(even)': { bgcolor: '#fafafa' },
                            transition: 'background-color 0.15s ease',
                          }}
                          onClick={() => {
                            setSelectedRowKey(rowKey);
                            setViewMode('split');
                          }}
                        >
                          <TableCell sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>
                            {matrixPage * matrixRowsPerPage + index + 1}
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={700} color="#0f172a" sx={{ '&:hover': { color: '#2563eb' } }}>
                                {row.institute_name}
                              </Typography>
                              {row.school_address && (
                                <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ maxWidth: 220, fontSize: '0.68rem' }}>
                                  {row.school_address}
                                </Typography>
                              )}
                              <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.68rem', fontWeight: 600 }}>
                                {row.tab_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="caption" fontWeight={700} color="#1e293b" display="block">
                                {row.coordinator_name || 'Not Assigned'}
                              </Typography>
                              <Chip
                                size="small"
                                label={row.status_by_team || 'Active'}
                                sx={{
                                  fontSize: '0.62rem',
                                  height: 18,
                                  fontWeight: 700,
                                  bgcolor: row.status_by_team === 'Dead' ? '#fee2e2' : row.status_by_team === 'Risk' ? '#ffedd5' : '#eff6ff',
                                  color: row.status_by_team === 'Dead' ? '#dc2626' : row.status_by_team === 'Risk' ? '#ea580c' : '#2563eb',
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={`${efficiency}%`}
                              sx={{
                                fontSize: '0.7rem',
                                height: 20,
                                fontWeight: 800,
                                bgcolor: row.is_not_created ? '#fee2e2' : efficiency > 50 ? '#dcfce7' : '#fef3c7',
                                color: row.is_not_created ? '#991b1b' : efficiency > 50 ? '#15803d' : '#92400e',
                              }}
                            />
                          </TableCell>
                          {MATRIX_FEATURE_COLUMNS.map((col) => (
                            <TableCell key={col.key} align="center" sx={{ py: 0.8 }}>
                              <MatrixPill status={evaluateFeatureStatus(row, col.key)} />
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Matrix Pagination */}
          <TablePagination
            rowsPerPageOptions={[25, 50, 100, 250]}
            component="div"
            count={filteredMatrixRows.length}
            rowsPerPage={matrixRowsPerPage}
            page={matrixPage}
            onPageChange={(e, newPage) => setMatrixPage(newPage)}
            onRowsPerPageChange={(e) => {
              setMatrixRowsPerPage(parseInt(e.target.value, 10));
              setMatrixPage(0);
            }}
            className="no-print"
          />
        </Paper>
      ) : viewMode === 'split' ? (
        <Grid container spacing={2.5}>
          {/* LEFT PANE: CLIENT LIST SELECTOR (35% - 40%) */}
          <Grid item xs={12} md={5} lg={4}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                backgroundColor: '#ffffff',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                height: { xs: 'auto', md: 'calc(100vh - 270px)' },
                minHeight: 560,
              }}
            >
              {/* Left Pane Search Header */}
              <Box sx={{ p: 1.5, borderBottom: '1px solid #f1f5f9', backgroundColor: '#f8fafc' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                    Institutions
                  </Typography>
                  <Chip
                    size="small"
                    label={`${visibleRows.length} / ${rowCount}`}
                    sx={{ fontWeight: 700, fontSize: '0.7rem', height: 20 }}
                  />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Quick filter on this page..."
                  value={quickSchoolSearch}
                  onChange={(e) => setQuickSchoolSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: quickSchoolSearch ? (
                      <IconButton size="small" onClick={() => setQuickSchoolSearch('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    ) : null,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#ffffff' } }}
                />
              </Box>

              {/* Scrollable List of School Cards with Lazy Loading */}
              <Box
                ref={listScrollRef}
                onScroll={handleListScroll}
                sx={{
                  flexGrow: 1,
                  overflowY: 'auto',
                  p: 1.5,
                  scrollBehavior: 'smooth',
                }}
              >
                {loading && rows.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                    <CircularProgress size={30} />
                  </Box>
                ) : visibleRows.length === 0 ? (
                  <Box textAlign="center" py={6}>
                    <Typography variant="body2" color="text.secondary">
                      No institutions found.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {visibleRows.map((row) => {
                      const rowKey = getRowKey(row);
                      const isSelected = activeRow && getRowKey(activeRow) === rowKey;
                      const efficiency = parseFloat(row.using_efficiency || 0);

                      return (
                        <Paper
                          key={rowKey}
                          elevation={0}
                          onClick={() => setSelectedRowKey(rowKey)}
                          sx={{
                            p: 1.5,
                            mb: 1.2,
                            borderRadius: 2.5,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: '2px solid',
                            borderColor: isSelected ? '#2563eb' : '#f1f5f9',
                            backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                            boxShadow: isSelected ? '0 4px 14px rgba(37,99,235,0.12)' : 'none',
                            '&:hover': {
                              borderColor: isSelected ? '#2563eb' : '#cbd5e1',
                              backgroundColor: isSelected ? '#eff6ff' : '#f8fafc',
                            },
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                            <Box flexGrow={1}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={800}
                                color={isSelected ? '#1e40af' : '#0f172a'}
                                lineHeight={1.3}
                                mb={0.5}
                              >
                                {row.institute_name || 'Unnamed Institution'}
                              </Typography>

                              <Box display="flex" alignItems="center" gap={0.8} flexWrap="wrap">
                                <Box display="flex" alignItems="center" gap={0.4}>
                                  <PersonOutlineIcon sx={{ fontSize: 14, color: '#64748b' }} />
                                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    {row.coordinator_name || 'Not Assigned'}
                                  </Typography>
                                </Box>

                                <Typography variant="caption" color="#cbd5e1">•</Typography>

                                <Typography variant="caption" color="text.secondary">
                                  {row.tab_name}
                                </Typography>
                              </Box>

                              {/* Institution Features Badges */}
                              <Stack direction="row" spacing={0.6} mt={0.8} flexWrap="wrap" useFlexGap>
                                <Chip
                                  size="small"
                                  label={row.payment_gateway_enabled ? 'Payment Gateway: Enabled' : 'Payment Gateway: Disabled'}
                                  sx={{
                                    fontSize: '0.62rem',
                                    height: 18,
                                    fontWeight: 700,
                                    bgcolor: row.payment_gateway_enabled ? '#dcfce7' : '#f1f5f9',
                                    color: row.payment_gateway_enabled ? '#15803d' : '#64748b',
                                  }}
                                />
                                {row.website_enabled && (
                                  <Chip
                                    size="small"
                                    label="Website"
                                    sx={{
                                      fontSize: '0.62rem',
                                      height: 18,
                                      fontWeight: 700,
                                      bgcolor: '#eff6ff',
                                      color: '#1d4ed8',
                                    }}
                                  />
                                )}
                                {row.biometric_enabled && (
                                  <Chip
                                    size="small"
                                    label="Biometric"
                                    sx={{
                                      fontSize: '0.62rem',
                                      height: 18,
                                      fontWeight: 700,
                                      bgcolor: '#faf5ff',
                                      color: '#7e22ce',
                                    }}
                                  />
                                )}
                              </Stack>
                            </Box>

                            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                              {row.is_not_created ? (
                                <Chip
                                  size="small"
                                  label="Not Created"
                                  sx={{
                                    fontSize: '0.65rem',
                                    height: 20,
                                    bgcolor: '#fee2e2',
                                    color: '#991b1b',
                                    fontWeight: 700,
                                  }}
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  label={`${efficiency}%`}
                                  sx={{
                                    fontSize: '0.7rem',
                                    height: 20,
                                    bgcolor: efficiency > 50 ? '#dcfce7' : '#fef3c7',
                                    color: efficiency > 50 ? '#15803d' : '#92400e',
                                    fontWeight: 800,
                                  }}
                                />
                              )}

                              {isSelected && (
                                <KeyboardArrowRightIcon sx={{ color: '#2563eb', fontSize: 20, mt: 0.5 }} />
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}

                    {/* Bottom Lazy Loading / End of List Indicator */}
                    {loadingMore && (
                      <Box display="flex" justifyContent="center" alignItems="center" py={2} gap={1}>
                        <CircularProgress size={20} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Loading more institutions...
                        </Typography>
                      </Box>
                    )}
                    {!loadingMore && hasMore && (
                      <Box textAlign="center" py={1}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={loadMore}
                          sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#64748b' }}
                        >
                          Scroll or click to load more
                        </Button>
                      </Box>
                    )}
                    {!hasMore && rows.length > 0 && (
                      <Box textAlign="center" py={1.5}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                          All {rowCount || rows.length} institutions loaded
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {/* Status Footer */}
              <Box
                sx={{
                  borderTop: '1px solid #f1f5f9',
                  px: 2,
                  py: 1,
                  backgroundColor: '#f8fafc',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Showing {visibleRows.length} of {rowCount} institutions
                </Typography>
                {hasMore && !loadingMore && (
                  <Typography variant="caption" color="primary" fontWeight={600}>
                    Scroll down for more
                  </Typography>
                )}
                {loadingMore && (
                  <Box display="flex" alignItems="center" gap={0.8}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      Loading...
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* RIGHT PANE: SELECTED CLIENT'S INTERACTIVE LIVE REPORT (60% - 65%) */}
          <Grid item xs={12} md={7} lg={8}>
            {activeRow ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#ffffff',
                  p: { xs: 2, sm: 3 },
                  height: { xs: 'auto', md: 'calc(100vh - 270px)' },
                  overflowY: 'auto',
                }}
              >
                {/* School Header Banner */}
                <Box
                  sx={{
                    p: 2,
                    mb: 2.5,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%)',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  <Box flexGrow={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <SchoolOutlinedIcon sx={{ color: '#2563eb', fontSize: 26 }} />
                      <Typography variant="h6" fontWeight={800} color="#0f172a">
                        {activeRow.institute_name || 'Institution Report'}
                      </Typography>
                    </Box>

                    {activeRow.school_address && (
                      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                        <LocationOnOutlinedIcon sx={{ color: '#64748b', fontSize: 16 }} />
                        <Typography variant="body2" color="text.secondary">
                          {activeRow.school_address}
                        </Typography>
                      </Box>
                    )}

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mt={1}>
                      <Chip
                        size="small"
                        icon={<PersonOutlineIcon style={{ fontSize: 14 }} />}
                        label={`Coordinator: ${activeRow.coordinator_name || 'Not Assigned'}`}
                        sx={{ bgcolor: '#ffffff', fontWeight: 700, fontSize: '0.75rem', border: '1px solid #cbd5e1' }}
                      />
                      <Chip
                        size="small"
                        label={`Year: ${activeRow.tab_name}`}
                        sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: '0.75rem' }}
                      />
                      {activeRow.is_not_created && (
                        <Chip
                          size="small"
                          label="Report Not Created"
                          sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.75rem' }}
                        />
                      )}
                    </Stack>
                  </Box>

                  {/* Overall Efficiency Score */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      textAlign: 'center',
                      minWidth: 130,
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.65rem">
                      Using Efficiency
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={900}
                      color={parseFloat(activeRow.using_efficiency || 0) > 50 ? '#15803d' : '#ea580c'}
                      my={0.2}
                    >
                      {activeRow.is_not_created ? '0%' : `${parseFloat(activeRow.using_efficiency || 0)}%`}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={activeRow.is_not_created ? 0 : parseFloat(activeRow.using_efficiency || 0)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: parseFloat(activeRow.using_efficiency || 0) > 50 ? '#22c55e' : '#f97316',
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Institution Features Adoption Overview */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.8,
                    mb: 2.5,
                    borderRadius: 2.5,
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2}>
                    <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                      Institution Features Status
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Configured enterprise modules & add-ons
                    </Typography>
                  </Box>

                  <Grid container spacing={1.5}>
                    {/* Payment Gateway Feature */}
                    <Grid item xs={12} sm={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: activeRow.payment_gateway_enabled ? '#bbf7d0' : '#e2e8f0',
                          backgroundColor: activeRow.payment_gateway_enabled ? '#f0fdf4' : '#f8fafc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <PaymentsIcon
                            sx={{
                              color: activeRow.payment_gateway_enabled ? '#16a34a' : '#94a3b8',
                              fontSize: 22,
                            }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={700} color="#0f172a">
                              Payment Gateway
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Online Fee Collection
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          size="small"
                          label={activeRow.payment_gateway_enabled ? 'Enabled' : 'Disabled'}
                          sx={{
                            fontWeight: 800,
                            fontSize: '0.7rem',
                            height: 22,
                            bgcolor: activeRow.payment_gateway_enabled ? '#dcfce7' : '#e2e8f0',
                            color: activeRow.payment_gateway_enabled ? '#15803d' : '#64748b',
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Website Feature */}
                    <Grid item xs={12} sm={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: activeRow.website_enabled ? '#bfdbfe' : '#e2e8f0',
                          backgroundColor: activeRow.website_enabled ? '#eff6ff' : '#f8fafc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <LanguageIcon
                            sx={{
                              color: activeRow.website_enabled ? '#2563eb' : '#94a3b8',
                              fontSize: 22,
                            }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={700} color="#0f172a">
                              Website
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              School Web Portal
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          size="small"
                          label={activeRow.website_enabled ? 'Enabled' : 'Disabled'}
                          sx={{
                            fontWeight: 800,
                            fontSize: '0.7rem',
                            height: 22,
                            bgcolor: activeRow.website_enabled ? '#dbeafe' : '#e2e8f0',
                            color: activeRow.website_enabled ? '#1d4ed8' : '#64748b',
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Biometric Feature */}
                    <Grid item xs={12} sm={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: activeRow.biometric_enabled ? '#e9d5ff' : '#e2e8f0',
                          backgroundColor: activeRow.biometric_enabled ? '#faf5ff' : '#f8fafc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <FingerprintIcon
                            sx={{
                              color: activeRow.biometric_enabled ? '#9333ea' : '#94a3b8',
                              fontSize: 22,
                            }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={700} color="#0f172a">
                              Biometric
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Attendance Devices
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          size="small"
                          label={activeRow.biometric_enabled ? 'Enabled' : 'Disabled'}
                          sx={{
                            fontWeight: 800,
                            fontSize: '0.7rem',
                            height: 22,
                            bgcolor: activeRow.biometric_enabled ? '#f3e8ff' : '#e2e8f0',
                            color: activeRow.biometric_enabled ? '#7e22ce' : '#64748b',
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Payment Gateway Usage & Collections Report */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2.5,
                    borderRadius: 2.5,
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccountBalanceWalletOutlinedIcon sx={{ color: '#059669', fontSize: 24 }} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
                          Payment Gateway Report
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Synced transaction volumes, fee collections, and payment modes
                        </Typography>
                      </Box>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        size="small"
                        label={activeRow.payment_gateway_enabled ? 'Gateway Enabled' : 'Gateway Disabled'}
                        sx={{
                          fontWeight: 800,
                          fontSize: '0.72rem',
                          bgcolor: activeRow.payment_gateway_enabled ? '#dcfce7' : '#f1f5f9',
                          color: activeRow.payment_gateway_enabled ? '#15803d' : '#64748b',
                        }}
                      />
                      {Boolean(activeRow.payment_gateway_data?.month_count) && (
                        <Chip
                          size="small"
                          label={`${activeRow.payment_gateway_data.month_count} Periods Recorded`}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            bgcolor: '#eff6ff',
                            color: '#1d4ed8',
                          }}
                        />
                      )}
                    </Stack>
                  </Box>

                  {/* Summary KPI Cards */}
                  <Grid container spacing={1.5} mb={2}>
                    <Grid item xs={12} sm={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderLeftWidth: 4,
                          borderLeftColor: '#2563eb',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                          TOTAL AMOUNT COLLECTED
                        </Typography>
                        <Typography variant="h6" fontWeight={800} color="#0f172a" mt={0.3}>
                          ₹ {formatMoney(activeRow.payment_gateway_data?.total_amount || 0)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderLeftWidth: 4,
                          borderLeftColor: '#16a34a',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                          TOTAL TRANSACTIONS
                        </Typography>
                        <Typography variant="h6" fontWeight={800} color="#0f172a" mt={0.3}>
                          {formatInt(activeRow.payment_gateway_data?.transaction_count || 0)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderLeftWidth: 4,
                          borderLeftColor: '#ea580c',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                          GATEWAY CHARGES / FEES
                        </Typography>
                        <Typography variant="h6" fontWeight={800} color="#0f172a" mt={0.3}>
                          ₹ {formatMoney(activeRow.payment_gateway_data?.total_transaction_fees || 0)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                {activeRow.is_not_created ? (
                  /* Not Created Clean Friendly Card */
                  <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      This institution is not yet created in the client track report for academic year {activeRow.tab_name}.
                    </Typography>
                    <Typography variant="caption">
                      Click "Sync Report" above or select another Academic Year to fetch historical usage data.
                    </Typography>
                  </Alert>
                ) : (
                  <>
                    {/* Visual Bar Chart for Active Client's Key Rates */}
                    <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 2.5, border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                      <Typography variant="subtitle2" fontWeight={800} color="#0f172a" mb={1}>
                        Feature Utilization Rates (%)
                      </Typography>
                      <Box sx={{ width: '100%', height: 160 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={clientChartData} margin={{ top: 10, right: 20, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                            <RechartsTooltip formatter={(val) => `${val}%`} />
                            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                              {clientChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>

                    {/* Vital Quick Numbers */}
                    <Grid container spacing={1.5} mb={2.5}>
                      <Grid item xs={6} sm={3}>
                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} fontSize="0.7rem">
                            STUDENT COUNT
                          </Typography>
                          <Typography variant="h6" fontWeight={800} color="#0f172a">
                            {activeRow.student_count || activeRow.report_data?.student_count || '-'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} fontSize="0.7rem">
                            LAST ATTENDANCE
                          </Typography>
                          <Typography variant="body2" fontWeight={700} color="#15803d" noWrap>
                            {activeRow.student_attendance_last_used_value || activeRow.attendance_last_used || '-'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} fontSize="0.7rem">
                            LAST HOMEWORK
                          </Typography>
                          <Typography variant="body2" fontWeight={700} color="#2563eb" noWrap>
                            {activeRow.student_diary_last_used_value || activeRow.homework_last_used || '-'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} fontSize="0.7rem">
                            LAST FEES COLLECTED
                          </Typography>
                          <Typography variant="body2" fontWeight={700} color="#b45309" noWrap>
                            {activeRow.fee_collection_last_used_value || '-'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Categorized Detailed Feature Health */}
                    <Grid container spacing={2} mb={2.5}>
                      {/* Academics & Classroom */}
                      <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                            <MenuBookOutlinedIcon sx={{ color: '#2563eb', fontSize: 18 }} />
                            <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                              Academics & Classroom
                            </Typography>
                          </Box>

                          <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Attendance Usage</Typography>
                              {renderMetricBadge(activeRow.student_attendance_last_used_value || activeRow.attendance_used_percentage, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Diary / Homework</Typography>
                              {renderMetricBadge(activeRow.student_diary_last_used_value || activeRow.diary_used_percentage, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Exam Schedule</Typography>
                              {renderMetricBadge(activeRow.exam_schedule_last_used_value || activeRow.last_exam_used_percentage, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Exam Marks</Typography>
                              {renderMetricBadge(activeRow.exam_marks_last_used_value, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" color="text.secondary">Timetable</Typography>
                              {renderMetricBadge(activeRow.timetable_last_used_value, activeRow.is_not_created)}
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>

                      {/* Operations & Finance */}
                      <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                            <AccountBalanceWalletOutlinedIcon sx={{ color: '#0d9488', fontSize: 18 }} />
                            <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                              Finance & Operations
                            </Typography>
                          </Box>

                          <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Fee Collection</Typography>
                              {renderMetricBadge(activeRow.fee_collection_last_used_value, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Fee Plan Done</Typography>
                              {renderMetricBadge(activeRow.fee_plan_done_value, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Staff Attendance</Typography>
                              {renderMetricBadge(activeRow.staff_attendance_last_marked_value, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Store / Items Sold</Typography>
                              {renderMetricBadge(activeRow.item_sold_last_marked_value, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" color="text.secondary">Expenses</Typography>
                              {renderMetricBadge(activeRow.expenses_last_marked_value, activeRow.is_not_created)}
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>

                      {/* Admissions & Front Desk */}
                      <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                            <FactCheckOutlinedIcon sx={{ color: '#7c3aed', fontSize: 18 }} />
                            <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                              Admissions & Front Desk
                            </Typography>
                          </Box>

                          <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Enquiry Form Used</Typography>
                              {renderMetricBadge(activeRow.enquiry_form_used_value, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Enquiry Last Used</Typography>
                              {renderMetricBadge(activeRow.enquiry_last_used_value, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Application Form Used</Typography>
                              {renderMetricBadge(activeRow.application_form_used_value, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" color="text.secondary">Admission Form Used</Typography>
                              {renderMetricBadge(activeRow.admission_form_used_value, activeRow.is_not_created)}
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>

                      {/* User Logins & Engagement */}
                      <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                            <PeopleAltOutlinedIcon sx={{ color: '#ea580c', fontSize: 18 }} />
                            <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                              Logins & User Activity
                            </Typography>
                          </Box>

                          <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Staff Active (7 Days)</Typography>
                              {renderMetricBadge(activeRow.staff_active_last_7_days_value, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Student Active (7 Days)</Typography>
                              {renderMetricBadge(activeRow.student_active_last_7_days_value, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" pb={0.5} borderBottom="1px solid #f8fafc">
                              <Typography variant="caption" color="text.secondary">Staff Login %</Typography>
                              {renderMetricBadge(activeRow.staff_login_percentage_value, activeRow.is_not_created)}
                            </Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" color="text.secondary">Student Login %</Typography>
                              {renderMetricBadge(activeRow.student_login_percentage_value, activeRow.is_not_created)}
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </>
                )}

                {/* Collapsible Section for All Raw Synced Columns */}
                <Box mt={2}>
                  <Button
                    size="small"
                    variant="text"
                    endIcon={<ExpandMoreIcon sx={{ transform: showRawColumns ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
                    onClick={() => setShowRawColumns(!showRawColumns)}
                    sx={{ textTransform: 'none', color: '#64748b', fontWeight: 700 }}
                  >
                    {showRawColumns ? 'Hide Full Raw Data Columns' : `View All Raw Synced Columns (${detailColumns.length})`}
                  </Button>

                  <Collapse in={showRawColumns}>
                    <Paper elevation={0} sx={{ p: 2, mt: 1, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#fbfdff' }}>
                      <Grid container spacing={1.5}>
                        {detailColumns.map((col) => (
                          <Grid item xs={12} sm={6} md={4} key={col.schemacolumn}>
                            <Box sx={{ borderBottom: '1px solid #f1f5f9', pb: 0.8 }}>
                              <Typography variant="caption" color="text.secondary" display="block" fontSize="0.7rem">
                                {col.column}
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="#0f172a">
                                {formatValue(activeRow[col.schemacolumn], col.schemacolumn, activeRow.is_not_created)}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Collapse>
                </Box>
              </Paper>
            ) : (
              <Box textAlign="center" py={12}>
                <Typography variant="body1" color="text.secondary">
                  Select a school from the left to view its detailed report.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      ) : (
        /* ============================================================== */
        /* 3. CLASSIC TABLE VIEW (FOR USERS WHO WANT FULL DATA GRID)      */
        /* ============================================================== */
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell width={48} />
                  {renderSortableHeader('tab_name', 'Academic Year')}
                  {summaryColumns.map((column) => (
                    renderSortableHeader(column.schemacolumn, column.column)
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={totalColumnCount}>
                      <Typography align="center" sx={{ py: 4 }}>Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : rows.length ? (
                  rows.map((row, index) => {
                    const rowKey = `${row.tab_name || ''}::${row.institute_name || ''}::${page}::${index}`;
                    const isExpanded = expandedRowId === rowKey;
                    return (
                      <React.Fragment key={rowKey}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => setExpandedRowId(isExpanded ? null : rowKey)}
                            >
                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{formatValue(row.tab_name)}</TableCell>
                          {summaryColumns.map((column) => (
                            <TableCell key={column.schemacolumn}>
                              {formatValue(row[column.schemacolumn], column.schemacolumn, row.is_not_created)}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ py: 0 }} colSpan={totalColumnCount}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, backgroundColor: '#fbfdff' }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                  More Report Data
                                </Typography>
                                <Grid container spacing={2}>
                                  {detailColumns.map((column) => (
                                    <Grid item xs={12} md={4} key={column.schemacolumn}>
                                      <Box sx={{ borderBottom: '1px solid #eef2f7', pb: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          {column.column}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                          {formatValue(row[column.schemacolumn], column.schemacolumn, row.is_not_created)}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={totalColumnCount}>
                      <Typography align="center" sx={{ py: 4 }}>No report data found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={rowCount}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={(event, newPage) => {
              setPage(newPage);
              fetchReports(newPage, false);
            }}
            onRowsPerPageChange={(event) => {
              setPageSize(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      {/* Mobile Navigation */}
      <ReportsMobileBottomNav />
    </Box>
  );
}

export default ClientReportList;
